/**
 * SearchableSelect — dropdown avec recherche en tête.
 *
 * - Bouton qui affiche la sélection actuelle + chevron
 * - Au clic : ouvre une popover avec input recherche + liste filtrée
 * - Click extérieur / ESC : ferme
 * - Pas de dépendance externe (volontaire — gain bundle)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

interface SearchableSelectProps<T> {
  value: T | null;
  options: T[];
  onChange: (v: T | null) => void;
  getKey: (o: T) => string | number;
  /** Texte affiché dans le bouton et dans la liste (peut contenir tout) */
  getLabel: (o: T) => string;
  /** Texte secondaire (sous-titre) affiché grisé dans la liste */
  getSubLabel?: (o: T) => string;
  /** Texte utilisé pour la recherche (par défaut = getLabel) */
  getSearchText?: (o: T) => string;
  placeholder?: string;
  /** Texte affiché si aucun option ne matche */
  emptyMessage?: string;
  /** Texte affiché si options.length === 0 */
  noOptionsMessage?: string;
  /** Permet d'effacer la sélection (icône X dans le bouton) */
  clearable?: boolean;
  /** required */
  required?: boolean;
  /** Classe additionnelle sur le bouton trigger */
  className?: string;
  /** disabled */
  disabled?: boolean;
}

export default function SearchableSelect<T>({
  value, options, onChange,
  getKey, getLabel, getSubLabel, getSearchText,
  placeholder = '— Sélectionner —',
  emptyMessage = 'Aucun résultat',
  noOptionsMessage = 'Aucune option disponible',
  clearable = false,
  required = false,
  className = '',
  disabled = false,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => {
      const t = (getSearchText || getLabel)(o).toLowerCase();
      return t.includes(q);
    });
  }, [options, query, getSearchText, getLabel]);

  // Click outside → ferme
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus auto-input à l'ouverture
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setActiveIdx(0);
    }
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const select = (opt: T | null) => {
    onChange(opt);
    setOpen(false);
    setQuery('');
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) select(opt);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button type="button" disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900
          text-gray-900 dark:text-gray-100 flex items-center gap-2 text-left
          ${disabled
            ? 'border-gray-200 dark:border-gray-700 opacity-60 cursor-not-allowed'
            : open
              ? 'border-blue-500 ring-2 ring-blue-500/30'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}>
        <span className={`flex-1 truncate ${value ? '' : 'text-gray-400 dark:text-gray-500'}`}>
          {value ? getLabel(value) : placeholder}
        </span>
        {clearable && value && !disabled && (
          <button type="button" onClick={(e) => { e.stopPropagation(); select(null); }}
            className="text-gray-400 hover:text-red-500" aria-label="Effacer">
            <X size={14} />
          </button>
        )}
        <ChevronDown size={14}
          className={`text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Champ caché pour la validation HTML5 (required) */}
      {required && (
        <input type="text" tabIndex={-1} required value={value ? String(getKey(value)) : ''}
          onChange={() => {}}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
      )}

      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white dark:bg-gray-800
                        border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg
                        max-h-72 flex flex-col">
          <div className="relative border-b border-gray-100 dark:border-gray-700">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input ref={inputRef}
              value={query} onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKey}
              placeholder="Rechercher…"
              className="w-full pl-8 pr-3 py-2 text-sm bg-transparent border-0
                         focus:outline-none text-gray-900 dark:text-gray-100
                         placeholder:text-gray-400" />
          </div>

          <div ref={listRef} className="overflow-y-auto flex-1">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-500 text-center">
                {noOptionsMessage}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-500 text-center">
                {emptyMessage} pour « {query} »
              </div>
            ) : (
              filtered.map((opt, idx) => {
                const isActive = idx === activeIdx;
                const isSelected = value && getKey(value) === getKey(opt);
                const sub = getSubLabel?.(opt);
                return (
                  <button key={String(getKey(opt))} type="button" data-idx={idx}
                    onClick={() => select(opt)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-start gap-2 ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    } ${isSelected ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-900 dark:text-gray-100'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{getLabel(opt)}</div>
                      {sub && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {sub}
                        </div>
                      )}
                    </div>
                    {isSelected && <Check size={13} className="flex-shrink-0 mt-0.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
